import React, { useState } from "react";
import axios from "axios";
import { brl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ink = "#f5f0e8";
const ink2 = "#cdc8be";
const ink3 = "#8a857a";
const gold = "#b8935a";
const line = "#2b2833";
const bg1 = "#0d0d0f";
const bg2 = "#15141a";
const red = "#c47a7a";
const inp = { background: bg1, border: `1px solid ${line}`, color: ink, padding: "7px 10px", fontSize: 13, borderRadius: 3, boxSizing: "border-box" };
const btn = { background: "transparent", color: ink2, border: `1px solid ${line}`, padding: "6px 12px", fontSize: 11, cursor: "pointer" };
const btnGold = { background: gold, color: bg1, border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const cap = { fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: ink3 };

const errMsg = (e) => e?.response?.data?.detail || e.message;

function RetrabalhoForm({ inicial, onSalvar, onCancelar, testid }) {
  const [descricao, setDescricao] = useState(inicial?.descricao || "");
  const [valor, setValor] = useState(inicial ? String(inicial.valor) : "");
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    const v = Math.abs(Number(String(valor).replace(",", ".")));
    if (!descricao.trim()) return alert("Descreva o problema.");
    if (!(v > 0)) return alert("Informe o valor do prejuízo.");
    setSalvando(true);
    try { await onSalvar({ descricao: descricao.trim(), valor: v }); } finally { setSalvando(false); }
  };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "8px 0" }} data-testid={testid}>
      <input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição do problema" style={{ ...inp, flex: "1 1 220px" }} data-testid={`${testid}-descricao`} />
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: red, fontSize: 13 }}>− R$</span>
        <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} placeholder="0,00" style={{ ...inp, width: 110 }} data-testid={`${testid}-valor`} />
      </div>
      <button onClick={onCancelar} style={btn} data-testid={`${testid}-cancelar`}>Cancelar</button>
      <button onClick={salvar} disabled={salvando} style={btnGold} data-testid={`${testid}-salvar`}>{salvando ? "Salvando…" : "Salvar"}</button>
    </div>
  );
}

function RetrabalhoItem({ r, podeEditar, onEditar, onRemover }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: `1px solid ${line}`, fontSize: 13 }} data-testid={`retrab-item-${r.id}`}>
      <span style={{ color: ink2, wordBreak: "break-word" }}>{r.descricao}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
        <span style={{ color: red, fontWeight: 500 }} data-testid={`retrab-valor-${r.id}`}>− {brl(r.valor)}</span>
        {podeEditar && <button onClick={onEditar} style={{ ...btn, padding: "2px 8px" }} data-testid={`btn-edit-retrab-${r.id}`}>Editar</button>}
        {podeEditar && <button onClick={onRemover} style={{ ...btn, padding: "2px 8px", color: red }} data-testid={`btn-del-retrab-${r.id}`}>×</button>}
      </div>
    </div>
  );
}

/* Retrabalhos de um bloco de visita: só admin/gestor lançam, editam ou removem. */
export function RetrabalhoBox({ g, podeEditar, obraId, headers, onReload }) {
  const [novo, setNovo] = useState(false);
  const [editId, setEditId] = useState(null);
  if (!podeEditar && g.retrabalhos.length === 0) return null;
  const url = `${API}/obras/${obraId}/servicos/retrabalhos`;
  const run = async (fn) => {
    try { await fn(); setNovo(false); setEditId(null); await onReload(); } catch (e) { alert("Erro: " + errMsg(e)); }
  };
  const criar = (b) => run(() => axios.post(url, { ...b, prestador_kind: g.kind, prestador_id: g.id, data: g.data }, { headers }));
  const editar = (id, b) => run(() => axios.patch(`${url}/${id}`, b, { headers }));
  const remover = (id) => window.confirm("Remover este retrabalho?") && run(() => axios.delete(`${url}/${id}`, { headers }));
  return (
    <div style={{ marginTop: 14, background: bg1, border: "1px solid rgba(139,58,58,0.4)", borderRadius: 4, padding: 12 }} data-testid={`retrab-box-${g.key}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={cap}>Retrabalho ({brl(g.total_retrab)})</div>
        {podeEditar && !novo && <button onClick={() => setNovo(true)} style={{ ...btn, color: red }} data-testid={`btn-add-retrab-${g.key}`}>+ Retrabalho</button>}
      </div>
      {g.retrabalhos.map(r => editId === r.id
        ? <RetrabalhoForm key={r.id} inicial={r} onSalvar={b => editar(r.id, b)} onCancelar={() => setEditId(null)} testid={`retrab-edit-form-${r.id}`} />
        : <RetrabalhoItem key={r.id} r={r} podeEditar={podeEditar} onEditar={() => setEditId(r.id)} onRemover={() => remover(r.id)} />)}
      {novo && <RetrabalhoForm onSalvar={criar} onCancelar={() => setNovo(false)} testid={`retrab-form-${g.key}`} />}
    </div>
  );
}

/* Resumo do saldo do bloco: Serviços − Adiantamentos − Retrabalho. */
export function SaldoBloco({ g }) {
  const item = (label, valor, cor, tid) => (
    <span>{label}: <strong style={{ color: cor }} data-testid={`${tid}-${g.key}`}>{valor}</strong></span>
  );
  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "4px 16px", marginTop: 12, fontSize: 12, color: ink3 }} data-testid={`saldo-bloco-${g.key}`}>
      {item("Serviços", brl(g.total), ink, "bloco-servicos")}
      {item("Adiantamentos", `− ${brl(g.total_adiant)}`, ink, "bloco-adiant")}
      {item("Retrabalho", `− ${brl(g.total_retrab)}`, red, "bloco-retrab")}
      {item("Saldo", brl(g.total - g.total_adiant - g.total_retrab), gold, "bloco-saldo")}
    </div>
  );
}

/* Recibo consolidado: todas as visitas + adiantamentos + retrabalhos do prestador na obra. */
export function ReciboConsolidadoBox({ obraId, grupos, headers, contatos, onLink, onClose }) {
  const resumos = grupos.filter(g => g.resumo);
  const [ref, setRef] = useState(resumos[0]?.ref || "");
  const [gerando, setGerando] = useState(false);
  const g = resumos.find(x => x.ref === ref);
  const gerarLink = async () => {
    setGerando(true);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/servicos/recibos`, { prestador_kind: g.kind, prestador_id: g.id, forma_pagamento: "PIX" }, { headers });
      onLink({ token: data.public_token, prestador_nome: g.nome, telefone: contatos(g)?.telefone || "", saldo: data.snapshot?.saldo });
    } catch (e) {
      alert("Erro: " + errMsg(e));
    } finally {
      setGerando(false);
    }
  };
  return (
    <div style={{ background: bg2, border: `1px solid ${gold}`, borderRadius: 6, padding: 18, marginBottom: 18 }} data-testid="recibo-consolidado-box">
      <div style={{ ...cap, color: gold, marginBottom: 10 }}>Recibo Consolidado · todas as visitas do prestador nesta obra</div>
      {resumos.length === 0 ? <div style={{ color: ink3, fontSize: 13 }}>Nenhum serviço registrado.</div> : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select value={ref} onChange={e => setRef(e.target.value)} style={{ ...inp, minWidth: 220 }} data-testid="consolidado-prestador-select">
            {resumos.map(x => <option key={x.ref} value={x.ref}>{x.nome}</option>)}
          </select>
          {g && <span style={{ fontSize: 12, color: ink3 }} data-testid="consolidado-saldo">Saldo geral: <strong style={{ color: gold }}>{brl(g.resumo.total - g.resumo.total_adiant - g.resumo.total_retrab)}</strong></span>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btn} data-testid="consolidado-fechar">Fechar</button>
          <button onClick={() => window.open(`/recibo/${obraId}/${g.kind}/${g.id}`, "_blank")} style={{ ...btn, color: ink }} data-testid="consolidado-ver">Ver / Imprimir</button>
          <button onClick={gerarLink} disabled={gerando} style={btnGold} data-testid="consolidado-gerar-link">{gerando ? "Gerando…" : "Gerar Link p/ Prestador"}</button>
        </div>
      )}
    </div>
  );
}

/* Seção de retrabalho no documento do recibo (impressão e link público). */
export function ReciboRetrabalhos({ retrabalhos }) {
  if (!retrabalhos || retrabalhos.length === 0) return null;
  const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
  return (
    <div style={{ marginBottom: 18 }} data-testid="rec-retrabalhos">
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#666", marginBottom: 8 }}>Retrabalho</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {retrabalhos.map(r => (
            <tr key={r.id} style={{ borderBottom: "1px solid #eaeaea" }}>
              <td style={{ padding: "6px", width: 90 }}>{fmt(r.data)}</td>
              <td style={{ padding: "6px", wordBreak: "break-word" }}>{r.descricao}</td>
              <td style={{ padding: "6px", textAlign: "right", color: "#a04040", whiteSpace: "nowrap" }}>− {brl(r.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
